import React, { useState, useEffect, useContext, useCallback, useMemo } from "react";
import axios from "axios";
import { AppContext } from "../../../../context/AppContext";
import "../../../../assets/css/patient/patientlayouts/layoutpages/MyNotifications.css";


const TYPE_ICONS = {
  booking:
    "M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z",
  payment:
    "M20 4H4c-1.11 0-1.99.89-1.99 2L2 18c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2zm0 14H4v-6h16v6zm0-10H4V6h16v2z",
};
const DEFAULT_ICON =
  "M12 22c1.1 0 2-.9 2-2h-4c0 1.1.89 2 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z";

const typeLabel = (type) => (type ? type.charAt(0).toUpperCase() + type.slice(1) : "Other");

const MyNotifications = () => {
  const { baseUrl } = useContext(AppContext);

  const [notifications, setNotifications] = useState([]);
  const [activeFilter, setActiveFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const [keepNotifications, setKeepNotifications] = useState(true);
  const [inAppNotifications, setInAppNotifications] = useState(true);
  const [prefsLoaded, setPrefsLoaded] = useState(false);

 
  const authHeaders = useCallback(() => {
    const token = localStorage.getItem("patient_token");
    return { Authorization: `Bearer ${token}`, Accept: "application/json" };
  }, []);

  // ==================================================
  // FETCH NOTIFICATIONS — real patient endpoint (flat shape)
  // ==================================================
  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);

      const response = await axios.get(`${baseUrl}/Patient/Notifications/list`, {
        headers: authHeaders(),
      });

      const list = response.data.data || [];

      const mapped = list.map((n) => ({
        id: n.id,
        type: n.type,
        title: n.title,
        message: n.message,
        time: new Date(n.created_at).toLocaleString(),
        read: !!n.is_read,
      }));

      setNotifications(mapped);
    } catch (error) {
      console.error("Fetch notifications error:", error);
    } finally {
      setLoading(false);
    }
  }, [baseUrl, authHeaders]);

  // ==================================================
  // FETCH PREFERENCES (on page load) — this endpoint was already correct
  // ==================================================
  const fetchPreferences = useCallback(async () => {
    try {
      const response = await axios.get(`${baseUrl}/notification-preferences`, {
        headers: authHeaders(),
      });

      setKeepNotifications(response.data.keep_notifications);
      setInAppNotifications(response.data.in_app_notifications);
    } catch (error) {
      console.error("Fetch preferences error:", error);
    } finally {
      setPrefsLoaded(true);
    }
  }, [baseUrl, authHeaders]);

  useEffect(() => {
    fetchNotifications();
    fetchPreferences();
  }, [fetchNotifications, fetchPreferences]);

  const unreadCount = notifications.filter((n) => !n.read).length;

  // Filters build themselves from whatever types actually show up —
  // no more hardcoded "Sessions/Exercises/Feedback" that never populate.
  const filters = useMemo(() => {
    const types = Array.from(new Set(notifications.map((n) => n.type).filter(Boolean)));
    return [{ key: "all", label: "All" }, ...types.map((t) => ({ key: t, label: typeLabel(t) }))];
  }, [notifications]);

  const visibleNotifications =
    activeFilter === "all" ? notifications : notifications.filter((n) => n.type === activeFilter);

  // ==================================================
  // MARK ONE AS READ (POST, matches PatientNotificationController)
  // ==================================================
  const markAsRead = async (id) => {
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));

    try {
      await axios.post(`${baseUrl}/Patient/Notifications/${id}/read`, {}, { headers: authHeaders() });
    } catch (error) {
      console.error("Mark as read error:", error);
    }
  };

  // ==================================================
  // MARK ALL AS READ (POST)
  // ==================================================
  const markAllAsRead = async () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));

    try {
      await axios.post(`${baseUrl}/Patient/Notifications/read-all`, {}, { headers: authHeaders() });
    } catch (error) {
      console.error("Mark all as read error:", error);
    }
  };

  // ==================================================
  // UPDATE PREFERENCES (called on every toggle click)
  // ==================================================
  const updatePreferences = async (nextKeep, nextInApp) => {
    try {
      await axios.put(
        `${baseUrl}/notification-preferences`,
        { keep_notifications: nextKeep, in_app_notifications: nextInApp },
        { headers: authHeaders() }
      );
    } catch (error) {
      console.error("Update preferences error:", error);
    }
  };

  const handleToggleKeep = () => {
    const next = !keepNotifications;
    setKeepNotifications(next);
    updatePreferences(next, inAppNotifications);
  };

  const handleToggleInApp = () => {
    const next = !inAppNotifications;
    setInAppNotifications(next);
    updatePreferences(keepNotifications, next);
  };

  return (
    <div className="notifications-container">
      {/* Header */}
      <div className="notifications-header">
        <div className="header-content">
          <span className="header-icon-wrap">
            <svg className="header-icon" viewBox="0 0 24 24" fill="currentColor">
              <path d={DEFAULT_ICON} />
            </svg>
          </span>
          <div>
            <h2 className="notifications-title">Notifications</h2>
            <p className="notifications-subtitle">Stay on top of your sessions, payments and updates</p>
          </div>
        </div>

        <div className="header-actions">
          <span className={`unread-badge ${unreadCount === 0 ? "zero" : ""}`}>
            {unreadCount} Unread
          </span>
          <button className="mark-all-btn" onClick={markAllAsRead} disabled={unreadCount === 0}>
            Mark all as read
          </button>
        </div>
      </div>

      {/* Notification List */}
      <div className="notification-list-section">
        <div className="list-section-head">
          <h3 className="list-section-title">
            <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
              <path d={DEFAULT_ICON} />
            </svg>
            Recent Notifications
          </h3>

          <div className="notification-filters">
            {filters.map((f) => (
              <button
                key={f.key}
                className={`filter-tab ${activeFilter === f.key ? "active" : ""}`}
                onClick={() => setActiveFilter(f.key)}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="notification-list">
            {[1, 2, 3].map((i) => (
              <div key={i} className="notification-card skeleton" />
            ))}
          </div>
        ) : visibleNotifications.length === 0 ? (
          <div className="notification-empty">
            <svg viewBox="0 0 24 24" width="44" height="44" fill="currentColor">
              <path d={DEFAULT_ICON} />
            </svg>
            <h4>You're all caught up</h4>
            <p>No notifications here yet.</p>
          </div>
        ) : (
          <div className="notification-list">
            {visibleNotifications.map((n) => (
              <div
                key={n.id}
                className={`notification-card ${!n.read ? "unread" : ""}`}
                onClick={() => !n.read && markAsRead(n.id)}
              >
                <div className={`notif-icon type-${n.type || "default"}`}>
                  <svg viewBox="0 0 24 24" fill="currentColor" width="19" height="19">
                    <path d={TYPE_ICONS[n.type] || DEFAULT_ICON} />
                  </svg>
                </div>
                <div className="notif-body">
                  <div className="notif-top-row">
                    <h4 className="notif-title">{n.title}</h4>
                    {!n.read && <span className="notif-dot" aria-label="unread" />}
                  </div>
                  <p className="notif-message">{n.message}</p>
                  <div className="notif-meta">
                    <span className={`notif-type-chip type-${n.type || "default"}`}>{typeLabel(n.type)}</span>
                    <span className="notif-time">{n.time}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Notification Preferences */}
      <div className="preferences-section">
        <h3 className="preferences-title">
          <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
            <path d="M19.14 12.94c.04-.3.06-.61.06-.94s-.02-.64-.07-.94l2.03-1.58a.49.49 0 0 0 .12-.61l-1.92-3.32a.49.49 0 0 0-.59-.22l-2.39.96a7.02 7.02 0 0 0-1.62-.94l-.36-2.54A.484.484 0 0 0 14 2h-4c-.25 0-.46.18-.49.42l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.23-.09-.49 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.07.62-.07.94s.02.64.07.94l-2.03 1.58a.49.49 0 0 0-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.37 1.03.7 1.62.94l.36 2.54c.05.24.26.42.49.42h4c.25 0 .46-.18.49-.42l.36-2.54c.59-.24 1.13-.57 1.62-.94l2.39.96c.23.09.49 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z" />
          </svg>
          Notification Preferences
        </h3>

        <div className="preferences-grid">
          <div className="preference-card">
            <div className="preference-info">
              <div className="preference-icon keep-icon">
                <svg viewBox="0 0 24 24" fill="currentColor" width="22" height="22">
                  <path d="M17 12h-5v5h5v-5zM16 1v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2h-1V1h-2zm3 18H5V8h14v11z" />
                </svg>
              </div>
              <div>
                <h4 className="preference-label">Keep Notifications</h4>
                <p className="preference-desc">Save notifications history so you can review them later</p>
              </div>
            </div>
            <label className="toggle-switch">
              <input type="checkbox" checked={keepNotifications} disabled={!prefsLoaded} onChange={handleToggleKeep} />
              <span className="toggle-slider"></span>
            </label>
          </div>

          <div className="preference-card">
            <div className="preference-info">
              <div className="preference-icon inapp-icon">
                <svg viewBox="0 0 24 24" fill="currentColor" width="22" height="22">
                  <path d={DEFAULT_ICON} />
                </svg>
              </div>
              <div>
                <h4 className="preference-label">In-App Notifications</h4>
                <p className="preference-desc">Show notifications inside the app when you open the dashboard</p>
              </div>
            </div>
            <label className="toggle-switch">
              <input type="checkbox" checked={inAppNotifications} disabled={!prefsLoaded} onChange={handleToggleInApp} />
              <span className="toggle-slider"></span>
            </label>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyNotifications;